import {
    getDaysInYear,
    getDateFromDayOfYear,
    formatDate,
    getDayName,
    isToday,
    isPast,
    getCurrentDayOfYear
} from "../lib/calendar";
import { escritsData } from "../data/escrits";
import { Button } from "./ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "./ui/tooltip";
import { useEffect } from "react";

const YEAR = 2026;
const TEMPLATE_URL = "https://docs.google.com/document/d/120YciDdvKAOKdoZ2MA0xIsQWylsp2GnQnyd5x-5VV-A/edit?tab=t.0";

interface DayDotProps {
    dayOfYear: number;
    hasEntry: boolean;
}

function DayDot({ dayOfYear, hasEntry }: DayDotProps) {
    const date = getDateFromDayOfYear(YEAR, dayOfYear);
    const isCurrentDay = isToday(dayOfYear, YEAR);
    const hasPassed = isPast(dayOfYear, YEAR);

    const handleClick = () => {
        if (hasEntry) {
            const searchQuery = formatDate(date);
            window.open(`https://docs.google.com/document/u/0/?q=${searchQuery}`, '_blank');
        } else {
            window.open(TEMPLATE_URL, '_blank');
        }
    };

    // Determine dot style based on state
    let dotClass = "w-3 h-3 rounded-full transition-all duration-200 cursor-pointer p-0 min-w-0";

    if (isCurrentDay) {
        dotClass += " bg-red-600 hover:bg-red-500 hover:scale-125 border-none";
    } else if (hasEntry) {
        dotClass += " bg-red-600 hover:bg-red-500 hover:scale-125 border-none";
    } else if (hasPassed) {
        dotClass += " bg-white hover:bg-gray-200 hover:scale-125 border-none";
    } else {
        dotClass += " bg-gray-600 hover:bg-gray-500 hover:scale-125 border-none";
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <button
                    onClick={handleClick}
                    className={dotClass}
                    aria-label={`${getDayName(date)}, ${formatDate(date)}`}
                />
            </TooltipTrigger>
            <TooltipContent className="bg-white text-black border-none shadow-lg px-3 py-2">
                <div className="text-xs font-semibold text-gray-900">{getDayName(date)}</div>
                <div className="text-sm font-medium">{formatDate(date)}</div>
            </TooltipContent>
        </Tooltip>
    );
}

export function Inici() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const totalDays = getDaysInYear(YEAR);
    const currentDay = getCurrentDayOfYear();
    const daysLeft = totalDays - currentDay;

    // Create a map of dates with entries for quick lookup
    const dateMap = new Map<string, boolean>();
    escritsData
        .filter(e => e.date && e.year === YEAR)
        .forEach(entry => {
            if (entry.date) {
                dateMap.set(entry.date, true);
            }
        });

    return (
        <TooltipProvider>
            <div className="min-h-screen bg-black text-white p-8">
                <div className="max-w-6xl mx-auto">
                    {/* Header with current date */}
                    <div className="flex justify-between items-start mb-12">
                        <div className="text-center flex-1">
                            <button
                                onClick={() => window.open(TEMPLATE_URL, '_blank')}
                                className="inline-block px-4 py-2 bg-white text-black rounded-md mb-8 hover:bg-gray-100 transition-colors cursor-pointer font-medium"
                            >
                                {new Date().toLocaleDateString('ca-ES', {
                                    weekday: 'long',
                                    year: '2-digit',
                                    month: '2-digit',
                                    day: '2-digit'
                                })}
                            </button>
                        </div>
                    </div>

                    {/* Calendar grid */}
                    <div className="grid grid-cols-23 gap-2 mb-8 justify-center mx-auto" style={{ maxWidth: '600px' }}>
                        {Array.from({ length: totalDays }, (_, i) => {
                            const dayOfYear = i + 1;
                            const date = getDateFromDayOfYear(YEAR, dayOfYear);
                            const formattedDate = formatDate(date);
                            const hasEntry = dateMap.has(formattedDate);
                            
                            return (
                                <DayDot key={dayOfYear} dayOfYear={dayOfYear} hasEntry={hasEntry} />
                            );
                        })}
                    </div>

                    {/* Days remaining */}
                    <button
                        onClick={() => window.open(TEMPLATE_URL, '_blank')}
                        className="w-full text-center text-gray-400 text-sm mt-8 hover:text-gray-200 transition-colors cursor-pointer"
                    >
                        {daysLeft} dies restants
                    </button>
                </div>
            </div>
        </TooltipProvider>
    );
}

